import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || movies.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((v) => (v + 1) % movies.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, movies.length]);

  if (movies.length === 0) return null;
  const active = movies[index];

  const go = (dir: number) => {
    setIndex((v) => (v + dir + movies.length) % movies.length);
  };

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[580px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} - 秋霞电影`}
            className={`h-full w-full object-cover ${i === index ? "animate-kenburns" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/30"></div>
        </div>
      ))}

      <div className="relative mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
        <div key={active.id} className="max-w-[600px] animate-fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              焦点推荐
            </span>
            {active.tags.slice(0, 2).map((t) => (
              <span key={t} className="rounded-md border border-background-100/40 px-2.5 py-1 text-xs text-background-100">
                {t}
              </span>
            ))}
          </div>
          <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h2>
          <p className="mt-3 text-sm text-background-200 md:text-base">
            {active.year} · {active.region} · {active.genres.join(" / ")} · 导演 {active.director}
          </p>
          <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-background-300 md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
              查看影片详情
            </Link>
            <Link
              to="/category/popular"
              className="inline-flex items-center gap-2 rounded-md border border-background-100/50 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
            >
              浏览热门影片
            </Link>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-100/40 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:left-6 md:flex cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-100/40 bg-primary-950/40 text-background-50 transition-colors hover:bg-primary-950/70 md:right-6 md:flex cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* indicators */}
      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-7 bg-accent-500" : "w-1.5 bg-background-100/60"}`}
          />
        ))}
      </div>
    </section>
  );
}